import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Loader2, Navigation, MapPin } from 'lucide-react';
import dayjs from 'dayjs';
import { api, apiErrorMessage } from '../apiClient';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const BRAND_ICON = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const DEFAULT_CENTER = [19.076, 72.877]; // Mumbai
const DEFAULT_ZOOM = 12;

function RecenterMap({ lat, lng }) {
  const map = useMap();
  useEffect(() => { map.setView([lat, lng], DEFAULT_ZOOM); }, [lat, lng, map]);
  return null;
}

export default function MapPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [geoStatus, setGeoStatus] = useState('pending');
  const [selectedId, setSelectedId] = useState(null);

  const fetchNearby = useCallback(async (lat, lng) => {
    try {
      const r = await api.get('/events/nearby', { params: { lat, lng, radius_km: 25 } });
      setEvents(r.data);
    } catch (e) {
      console.log('nearby error', apiErrorMessage(e));
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) {
      setGeoStatus('denied');
      fetchNearby(DEFAULT_CENTER[0], DEFAULT_CENTER[1]);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCenter([pos.coords.latitude, pos.coords.longitude]);
        setGeoStatus('granted');
        fetchNearby(pos.coords.latitude, pos.coords.longitude);
      },
      () => {
        setGeoStatus('denied');
        fetchNearby(DEFAULT_CENTER[0], DEFAULT_CENTER[1]);
      },
      { timeout: 8000 }
    );
  }, [fetchNearby]);

  const selected = events.find((e) => e.id === selectedId);

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] md:flex-row" data-testid="map-page">
      {/* Map */}
      <div className="flex-1 relative min-h-[300px]">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
            <Loader2 className="w-8 h-8 animate-spin text-brand-magenta" />
          </div>
        ) : (
          <MapContainer center={center} zoom={DEFAULT_ZOOM} className="h-full w-full z-0" scrollWheelZoom>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <RecenterMap lat={center[0]} lng={center[1]} />
            {events.map((ev) => (
              ev.venue_lat && ev.venue_lng && (
                <Marker
                  key={ev.id}
                  position={[ev.venue_lat, ev.venue_lng]}
                  icon={BRAND_ICON}
                  eventHandlers={{ click: () => setSelectedId(ev.id) }}
                >
                  <Popup>
                    <strong>{ev.title}</strong><br />
                    <span className="text-xs">{ev.venue_name}</span>
                  </Popup>
                </Marker>
              )
            ))}
          </MapContainer>
        )}

        {geoStatus === 'denied' && (
          <div className="absolute top-3 left-3 right-3 z-20 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-2 rounded-xl flex items-center gap-2">
            <Navigation className="w-3.5 h-3.5 flex-shrink-0" />
            Location access denied — showing Mumbai default
          </div>
        )}
      </div>

      {/* Sidebar / Bottom card list */}
      <div className="md:w-96 bg-white border-t md:border-t-0 md:border-l border-gray-200 overflow-y-auto max-h-[40vh] md:max-h-full">
        <div className="px-4 py-3 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-base font-black text-gray-900">Nearby Events</h2>
          <p className="text-xs text-gray-500">{events.length} event{events.length === 1 ? '' : 's'} within 25 km</p>
        </div>

        {events.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
            <MapPin className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-sm font-bold text-gray-900">No events nearby</p>
            <p className="text-xs text-gray-500 mt-1">Try zooming out or checking a different area.</p>
          </div>
        )}

        <div className="flex flex-col">
          {events.map((ev) => (
            <button
              key={ev.id}
              data-testid={`map-event-${ev.id}`}
              onClick={() => { setSelectedId(ev.id); navigate(`/event/${ev.id}`); }}
              className={`flex items-center gap-3 px-4 py-3 text-left border-b border-gray-50 hover:bg-gray-50 transition-colors ${selectedId === ev.id ? 'bg-pink-50' : ''}`}
            >
              <img src={ev.banner_url} alt="" className="w-14 h-14 rounded-xl object-cover bg-gray-100 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-extrabold text-gray-900 truncate">{ev.title}</p>
                <p className="text-xs text-gray-500 truncate">{ev.venue_name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[11px] text-brand-magenta font-bold">{ev.distance_km != null ? `${ev.distance_km.toFixed(1)} km` : ev.city}</span>
                  <span className="text-[11px] text-gray-400">{dayjs(ev.start_datetime).format('MMM D')}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
